import { BulkUpdateStack, BulkUpdateStacksResult, CreateStack, LightStackHistory, ScopeLevelStackType, Stack, StackStatus, StackType } from '@pubstack/common/src/stack'
import { useQueryClient } from '@tanstack/react-query'
import { buildUrlWithSearchParams } from '~/utils/url'
import { get, MutationToasts, useMutation, useQuery } from './api-client'

export const STACKS_QUERY_KEYS = ['stacks']

//-------------------------------------------------------------------------
// Queries
//-------------------------------------------------------------------------
export const useStacks = ({ siteId, status, type }: { siteId?: string; status?: StackStatus[]; type?: StackType }) => {
  const url = buildUrlWithSearchParams('/stacks', {
    siteId,
    status,
    type,
  })
  const query = useQuery<Stack[]>({ url, keys: [...STACKS_QUERY_KEYS, { siteId, status, type }], enabled: !!siteId })

  return { ...query, data: query.data || [] }
}

/**
 * The scope's own stack of a scope-level type (`pbstck-bidder`, `spark-for-pub`).
 *
 * These carry no `siteId` — they are the scope's, not a media's — which is why they are absent from every
 * media stack list and why this asks by type alone. The live version is what the Spark tabs link to: a
 * `to-deploy` one describes what the next deploy will publish, not what Prebid Server is answering with
 * today, and the reader opening it wants the running configuration.
 */
export const useScopeLevelStack = (type: ScopeLevelStackType, enabled = true) => {
  const query = useQuery<Stack[]>({ url: buildUrlWithSearchParams('/stacks', { type }), keys: [...STACKS_QUERY_KEYS, 'scope-level', type], enabled })
  const stacks = query.data ?? []
  return { ...query, stack: stacks.find(({ status }) => status === 'live') ?? stacks[0] }
}

export const useStack = (id?: string) => {
  return useQuery<Stack>({ url: `/stacks/${id}`, keys: [...STACKS_QUERY_KEYS, ...(id ? [id] : [])], enabled: !!id })
}

export const useLiveStackHistory = (siteId: string) => {
  return useQuery<LightStackHistory[]>({
    url: `/stacks/history/${siteId}`,
    keys: [...STACKS_QUERY_KEYS, 'history', siteId],
    enabled: !!siteId,
  })
}

/** The history list carries no snapshot/output: fetch the full revision when one is opened. */
export const useFetchStackHistoryEntry = () => {
  const queryClient = useQueryClient()

  return (entryId: string) =>
    queryClient.fetchQuery({
      queryKey: [...STACKS_QUERY_KEYS, 'history', 'entry', entryId],
      queryFn: () => get<Stack>(`/stacks/history/entry/${entryId}`),
    })
}

//-------------------------------------------------------------------------
// Mutations
//-------------------------------------------------------------------------
export const useCreateStack = (toasts: MutationToasts = {}) => {
  return useMutation<CreateStack, Stack>({
    method: 'post',
    url: '/stacks',
    collectionKeys: STACKS_QUERY_KEYS,
    ...toasts,
  })
}

export const useBulkCreateStacks = (toasts: MutationToasts = {}) => {
  return useMutation<CreateStack[], Stack[]>({
    method: 'post',
    url: '/stacks/bulk',
    collectionKeys: STACKS_QUERY_KEYS,
    ...toasts,
  })
}

// Mainly used when updating priorities. Bulk updates is dedicated to Deployment center operations
export const useUpdateStacks = (toasts: MutationToasts = {}) => {
  return useMutation<Stack[]>({
    method: 'put',
    url: `/stacks`,
    collectionKeys: STACKS_QUERY_KEYS,
    ...toasts,
  })
}

export const toBulkUpdate = ({ id, status, ratio, updatedAt, priority }: Stack): BulkUpdateStack => ({ id, status, ratio, updatedAt, priority })

export const useBulkUpdateStacks = (toasts: MutationToasts = {}) => {
  return useMutation<BulkUpdateStack[], BulkUpdateStacksResult>({
    method: 'put',
    url: '/stacks/bulk',
    collectionKeys: STACKS_QUERY_KEYS,
    ...toasts,
  })
}
